import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Overline } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../data/contact";

export const LEGAL_UPDATED = "1 October 2026";

// Shared shell for /privacy-policy and /terms — long-form legal typesetting in the site aesthetic.
export default function LegalPage({ title, overline, intro, sections, path, description, testid }) {
  return (
    <div className="noise" data-testid={testid}>
      <Seo title={`${title} | Speedy Resurfacing`} description={description} path={path} />
      <section className="relative overflow-hidden pt-28 pb-10 md:pt-36 md:pb-14">
        <div className="stage-spot stage-spot-a" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <Overline className="text-gold">{overline}</Overline>
          <h1 className="mt-5 font-heading font-black text-4xl leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl" data-testid={`${testid}-title`}>{title}</h1>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500" data-testid={`${testid}-updated`}>Last updated {LEGAL_UPDATED}</p>
          <p className="mt-7 text-base md:text-lg leading-relaxed text-zinc-300">{intro}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 pb-24 md:px-10">
        <nav aria-label="On this page" className="mb-12 rounded-lg border border-white/10 bg-obsidian-800 p-6" data-testid={`${testid}-toc`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">On this page</p>
          <ol className="mt-4 grid gap-2 sm:grid-cols-2">
            {sections.map((s, i) => (
              <li key={s.id}><a href={`#${s.id}`} className="text-sm text-zinc-300 hover:text-gold transition-colors"><span className="font-mono text-gold/70 mr-2">{String(i + 1).padStart(2, "0")}</span>{s.h}</a></li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {sections.map((s, i) => (
            <article key={s.id} id={s.id} className="scroll-mt-28" data-testid={`${testid}-section-${s.id}`}>
              <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight"><span className="gold-text mr-3">{String(i + 1).padStart(2, "0")}</span>{s.h}</h2>
              <div className="mt-5 space-y-4">
                {s.p.map((para, j) =>
                  Array.isArray(para) ? (
                    <ul key={j} className="space-y-2.5">
                      {para.map((it) => (
                        <li key={it} className="flex gap-3 text-[15px] md:text-base leading-relaxed text-zinc-300"><span className="mt-2.5 h-1 w-5 shrink-0 bg-gold" />{it}</li>
                      ))}
                    </ul>
                  ) : (
                    <p key={j} className="text-[15px] md:text-base leading-relaxed text-zinc-300">{para}</p>
                  )
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 rounded-lg border border-gold/30 bg-obsidian-800 p-7" data-testid={`${testid}-contact`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Contact</p>
          <p className="mt-3 font-heading font-bold text-xl">Speedy Resurfacing — a division of Speedy Line Marking</p>
          <p className="mt-2 text-sm text-zinc-400">545 Merrylands Road, Merrylands NSW 2160, Australia</p>
          <p className="mt-1 text-sm text-zinc-400">
            <a href={PHONE_TEL} className="hover:text-gold">{PHONE_DISPLAY}</a> · <a href={EMAIL_MAILTO} className="hover:text-gold">{EMAIL}</a>
          </p>
          <p className="mt-4 text-xs text-zinc-500">
            See also: <Link to="/privacy-policy" className="text-gold hover:underline underline-offset-4">Privacy Policy</Link> · <Link to="/terms" className="text-gold hover:underline underline-offset-4">Terms of Use</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
